import { useEffect, useRef } from "react";
import BarcodeReaderComponent from "../../components/BarcodeReader";
import { useDrawerContext } from "../../context/drawerContext";
import IncidentIcon from "/public/images/icons/incidentIconWhite.svg?react";
import LocationPinIcon from "/public/images/icons/locationPin.svg?react";
import { useHeaderContext } from "../../context/headerContext";
import { useLocation } from "react-router-dom";
import { useUserContext } from "../../context/userContext";
import Button from "../../components/Button";
import { useNavigate } from "react-router-dom";
import CloseIcon from "/public/images/icons/close.svg?react";
import SelectInput from "../../components/SelectInput";
import { useState } from "react";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function Scan() {
  const navigate = useNavigate();
  const { setTitle } = useHeaderContext();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { selectedZone, setSelectedZone, selectedVenue, setSelectedVenue, user, logout, updateTokens } = useUserContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const [ isInitialised, setIsInitialised ] = useState(false);
  const [scanType, setScanType] = useState(queryParams.get('type') || 'normal');
  const scanTypeRef = useRef(scanType); 

  useEffect(() => {
    scanTypeRef.current = scanType;
  }, [scanType]);

  async function finishScanning(detections) {
    const url = detections[0].data;
    let iceid = null;
    let currentScanType = scanTypeRef.current; 

    if (url.includes("https://iceid.app?")) {
      iceid = url.split("https://iceid.app?")[1];
    } else if (url.includes("https://iceid.app/?")) {
      iceid = url.split("https://iceid.app/?")[1];
    } else if (url.includes("https://iceid.app/")) {
      iceid = url.split("https://iceid.app/")[1];
    }

    //playBeep();

    const req = await Sentry.startSpan({ name: "Band Scan Request", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Scan/BandScan?ScanID=${iceid}&OrgID=${selectedVenue?.OrgID}`;
        span.setAttribute("http.url", url);
        span.setAttribute("scanId", iceid);
    
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + user.token,
            },
          }, logout, updateTokens, (message) => { toast.info(message);}
        );
    
        if (!response.ok) {
          const errorText = await response.text();
          LogErrors(user, logout, updateTokens, `Failed to scan band. Status: ${response.status}, Response: ${errorText}`);
          span.setStatus("error");

          if (response.status === 422 || response.status === 400) {
            const message = errorText.replace(/^"|"$/g, '');
            setOpen(true);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">ERROR</h2>
                  <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
                </div>
                <div className="mt-6 flex flex-col gap-2 justify-stretch">
                  <h3 className="text-[24px]">Unable to process scan</h3>
                  <p className="text-sportsDarkGrey">{message}</p>
                  <Button variant="quaternary" onClick={() => setOpen(false)}>Close</Button>
                </div>
              </div>
            );
            return null;
          }

          throw new Error(`Failed to scan band: ${errorText}`);
        }
    
        span.setStatus("ok");
        return response;
      } catch (error) {
        Sentry.captureException(error, { extra: { scanId: iceid } });
        LogErrors(user, logout, updateTokens, "Error scanning band:", error);
        span.setStatus("error");
        throw error;
      } finally {
        span.end();
      }
    });

    if (!req) {
      return;
    }

    if (!req.ok) {
      setOpen(false);
      navigate("/scan/error");
    }

    const res = await req.json();
    setOpen(false);
    if(res.BandStateVerbose === "NOT ACTIVATED" || res.BandStateVerbose === "PERMANENTLY DISABLED" || res.BandStateVerbose === "LOST"){
      setOpen(false);
      navigate("/scan/error", { state: { band: res } });
    } else {
      if(!res.ParticipantId){
        setOpen(true);
        setDrawerContent(
          <div className="w-full">
            <div className="flex items-center justify-between w-full">
              <br />
              <h2 className="leading-5 tracking-widest uppercase text-[12px]">ERROR</h2>
              <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
            </div>
            <div className="mt-6 flex flex-col gap-2 justify-stretch">
              <h3 className="text-[24px]">
                Guest band not assigned.
              </h3>
              <p className=" text-sportsDarkGrey">The scanned band is not currently assigned to anyone.</p>
              <p className=" text-sportsDarkGrey">Please assign a guest band then scan again.</p>
              <Button variant="quaternary" onClick={() => { setOpen(false);}}>Close</Button>
            </div>
          </div>
        );
      }
      else if (currentScanType === "normal") {
        navigate("/scan/normal", { state: { band: res } });
      } else if (currentScanType === "emergency") { 
        if (!res.CurrentZone) {
          setOpen(true);
          setDrawerContent(
            <div className="w-full scan-drawer">
                <div className="flex items-center justify-between w-full">
                    <br />
                    <h2 className="leading-5 font-semibold uppercase text-[24px] text-sportsRed">
                      EMERGENCY SCAN
                    </h2>
                    <CloseIcon className='cursor-pointer' onClick={() => scanDrawer()} />
                </div>
                <div className="mt-6 flex flex-col gap-4 justify-stretch">
                    <h3 className="text-[24px]">Error scanning band</h3>
                    <p className=" text-sportsDarkGrey">The scanned band is not currently checked in to a zone.</p>
                    <p>Please check the band in on the normal scan screen then select Emergency Scan.</p>
                    <Button variant="primary" onClick={() => cancelEmergencyScan()}>Normal Scan</Button>
                    <Button variant="quaternary" onClick={() => scanDrawer()}>Close</Button>
                </div>
            </div>
          );
        } else {
          navigate("/scan/emergency", { state: { band: res } });
        }
      }
    }
  }

  function playBeep() {
    const context = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();
  
    oscillator.connect(gainNode);
    gainNode.connect(context.destination);
  
    oscillator.type = 'square';
    oscillator.frequency.value = 880; // A5 (higher pitch)
    gainNode.gain.value = 0.1;
  
    oscillator.start();
    oscillator.stop(context.currentTime + 0.1); // 100ms beep
  }

  function onDetected(detections) {
    sessionStorage.setItem("lastCodeScanned", detections[0].data);
    finishScanning(detections);
  }

  const triggerZoneSelectionDrawer = async () => {
    const venueReq = await Sentry.startSpan({ name: "Fetch Venue", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Venue/FetchVenue?OrgID=${selectedVenue?.OrgID}`;
        span.setAttribute("http.url", url);
        span.setAttribute("OrgID", selectedVenue?.OrgID);
  
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message);}
        );
  
        if (!response.ok) {
          const errorText = await response.text();
          LogErrors(user, logout, updateTokens, `Failed to fetch venue. Status: ${response.status}, Response: ${errorText}`);
          span.setStatus("error");
          throw new Error(`Failed to fetch venue: ${errorText}`);
        }
  
        const venueRes = await response.json();
        span.setStatus("ok");
        return venueRes;
      } catch (error) {
        Sentry.captureException(error, { extra: { OrgID: selectedVenue?.OrgID } });
        LogErrors(user, logout, updateTokens, "Error fetching venue: " + error);
        span.setStatus("error");
        throw error;
      } finally {
        span.end();
      }
    });
  
    if (venueReq) {
      const zoneReq = await Sentry.startSpan({ name: "Fetch Active Zones", op: "http.request" }, async (span) => {
        try {
          const url = `${import.meta.env.VITE_API_URL}Zone/FetchActiveOrgZones?OrgID=${selectedVenue?.OrgID}`;
          span.setAttribute("http.url", url);
          span.setAttribute("OrgID", selectedVenue?.OrgID);
  
          const response = await fetchWithRefresh(
            url,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message);}
          );
  
          if (!response.ok) {
            const errorText = await response.text();
            LogErrors(user, logout, updateTokens, `Failed to fetch zones. Status: ${response.status}, Response: ${errorText}`);
            span.setStatus("error");
            throw new Error(`Failed to fetch zones: ${errorText}`);
          }
  
          const zoneRes = await response.json();
          span.setStatus("ok");
          return zoneRes;
        } catch (error) {
          Sentry.captureException(error, { extra: { OrgID: selectedVenue?.OrgID } });
          LogErrors(user, logout, updateTokens, "Error fetching zones: " + error);
          span.setStatus("error");
          throw error;
        } finally {
          span.end();
        }
      });
  
      if (zoneReq) {
        const venueWithZones = {
          ...venueReq,
          zones: zoneReq.Zones
        };
  
        setSelectedVenue(venueWithZones);
        setOpen(true);
        setDrawerContent(
          <div className="w-full">
            <div className="flex items-center justify-between w-full">
              <br />
              <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                Choose Zone
              </h2>
              <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
            </div>
            <div className="mt-6 flex flex-col gap-4 justify-stretch">
              <h3 className="text-[24px]">
                Please choose the zone you are currently located at
              </h3>
              <p className=" text-sportsDarkGrey">
                You can update your zone anytime from the Home or Scan screens.
              </p>
              <form className="flex flex-col gap-4" onSubmit={setZone}>
                <SelectInput name="zoneSelect" className={'capitalize'} options={venueWithZones?.zones?.map(zone => (<option key={zone.ZoneID} value={JSON.stringify(zone)}>{zone.ZoneName}</option>))} label={'Your Current Zone'} placeholder={'Please Select'} />
                <Button submit>
                  Confirm & Continue
                </Button>
              </form>
            </div>
          </div>
        );
      }
    }
  };

  const setZone = (event) => {
    event.preventDefault();
    const selectedZone = JSON.parse(event.target.zoneSelect.value);
    setSelectedZone(selectedZone);
    setOpen(false);
  };

  const cancelEmergencyScan = () => {
    setOpen(false);
    setScanType("normal");
    navigate("/scan?type=normal");
  }

  const goToEmergencyScan = () => {
    setOpen(false);
    setDrawerContent(<div></div>);
    setScanType("emergency");
    navigate("/scan?type=emergency");
  }

  const cancelCheckoutScan = () => {
    setOpen(false);
    navigate("/");
  }

  const scanDrawer = () => {
    setOpen(true);

    if(scanType === "normal") {
      setDrawerContent(
        <div className="w-full scan-drawer">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              SCAN BAND
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => {navigate('/'); setOpen(false);}} />
          </div>
          <div className="mt-6 flex flex-col gap-2 justify-stretch">
            <p>Currently checking in at</p>
            <p className="flex mb-4 gap-2" onClick={selectedVenue?.zones.length !== 1 && triggerZoneSelectionDrawer}><LocationPinIcon /><span className="border-b border-b-black">{selectedZone?.ZoneName}</span></p>
            <div className="dashed-border w-full h-0.5 mb-4" />
            <Button className='!py-5' status="emergency" onClick={goToEmergencyScan}><IncidentIcon />Emergency Scan</Button>
          </div>
        </div>
      )
    } else if (scanType === "emergency") {
      setDrawerContent(
        <div className="w-full scan-drawer">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 font-semibold uppercase text-[24px] text-sportsRed">
              EMERGENCY SCAN
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => {navigate('/'); setOpen(false);}} />
          </div>
          <div className="mt-6 flex flex-col gap-2 justify-stretch">
            <p className="text-[#101820] font-medium">Declaring an emergency at</p>
            <p className="flex mb-4 gap-2" onClick={selectedVenue?.zones.length !== 1 && triggerZoneSelectionDrawer}><LocationPinIcon /><span className="border-b border-b-black">{selectedZone?.ZoneName}</span></p>
            <div className="dashed-border w-full h-0.5 mb-4" />
            <Button variant="quaternary" onClick={cancelEmergencyScan}>Cancel</Button>
          </div>
        </div>
      )
    } else if (scanType === "checkout") {
      setDrawerContent(
        <div className="w-full scan-drawer">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              SCAN BAND
            </h2>
            <CloseIcon onClick={cancelCheckoutScan} />
          </div>
          <div className="mt-6 flex flex-col gap-2 justify-stretch">
            <p>Currently checking out of</p>
            <p className="flex mb-4 gap-2"><LocationPinIcon /><span className="border-b border-b-black">{selectedZone?.ZoneName}</span></p>
            <div className="dashed-border w-full h-0.5 mb-4" />
            <Button variant="primary" onClick={cancelCheckoutScan}>Close</Button>
          </div>
        </div>
      )
    }
  }

  useEffect(() => {
    if (!isInitialised) return;

    setTitle("");
    scanDrawer();


  }, [selectedZone, scanType, isInitialised]);

  return (
    <div className="w-full h-svh">
      <BarcodeReaderComponent pageSetIsInitialised={setIsInitialised} onDetected={(e) => onDetected(e)} />
    </div>
  );
}